import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Settings2, ShieldAlert, Link2, Wand2, Sparkles, Terminal } from 'lucide-react';
import type { WizardConfig } from '../defaults';
import type { CsvDelimiter } from '../types';
import {
  ALLOWED_POST_IMPORT_COMMANDS,
  CONFLICT_ENTITIES,
  CONFLICT_POLICY_OPTIONS,
  CSV_DELIMITER_OPTIONS,
  EMAIL_MODE_OPTIONS,
  ENCODING_OPTIONS,
  NAME_MODE_OPTIONS,
  PARENT_TO_STUDENT_OPTIONS,
  PHONE_MODE_OPTIONS,
  STUDENT_TO_CLASS_OPTIONS,
  TEACHER_TO_SUBJECT_OPTIONS,
} from '../stepCatalog';

interface ConfigFormProps {
  config: WizardConfig;
  onChange: (config: WizardConfig) => void;
  /**
   * Des fichiers proviennent d'un classeur Excel converti en CSV (délimiteur
   * « ; ») : le choix du délimiteur est verrouillé pour rester cohérent.
   */
  delimiterLocked?: boolean;
  /**
   * Séparateur détecté dans les fichiers déposés (un export Google Sheets est
   * en virgules, nos modèles en « ; ») : la config a été alignée dessus, on le
   * dit plutôt que de le faire en silence.
   */
  detectedDelimiter?: CsvDelimiter | null;
}

// ---------- petits champs réutilisables ----------

function SelectField<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled,
  hint,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (v: T) => void;
  disabled?: boolean;
  hint?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      <Select value={value} onValueChange={(v) => onChange(v as T)} disabled={disabled}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function SwitchField({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-1">
      <div>
        <Label className="text-sm">{label}</Label>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

function NumberField({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min?: number;
  max?: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      <Input
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}

const delimiterLabel = (value: CsvDelimiter) =>
  CSV_DELIMITER_OPTIONS.find((o) => o.value === value)?.label ?? value;

export function ConfigForm({
  config,
  onChange,
  delimiterLocked,
  detectedDelimiter,
}: ConfigFormProps) {
  const set = <K extends keyof WizardConfig>(key: K, value: WizardConfig[K]) =>
    onChange({ ...config, [key]: value });

  return (
    <div className="space-y-6">
      {/* Lecture des fichiers CSV */}
      <Card>
        <CardHeader icon={<Settings2 />} iconColor="blue">
          <CardTitle className="text-base">Lecture des fichiers CSV</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <SelectField
            label="Délimiteur"
            value={config.csvDelimiter}
            options={CSV_DELIMITER_OPTIONS}
            onChange={(v) => set('csvDelimiter', v)}
            disabled={delimiterLocked}
            hint={
              delimiterLocked
                ? 'Verrouillé : un classeur Excel a été converti en CSV avec « ; ».'
                : detectedDelimiter
                  ? `Détecté dans les fichiers déposés : ${delimiterLabel(detectedDelimiter)}.`
                  : undefined
            }
          />
          <SelectField
            label="Encodage"
            value={config.encoding}
            options={ENCODING_OPTIONS}
            onChange={(v) => set('encoding', v)}
          />
          <NumberField
            label="Taille de lot (batch)"
            value={config.batchSize}
            min={1}
            max={5000}
            onChange={(v) => set('batchSize', v)}
          />
          <div className="space-y-2">
            <SwitchField
              label="Ignorer la ligne d’en-tête"
              checked={config.skipHeaderRow}
              onChange={(v) => set('skipHeaderRow', v)}
            />
            <SwitchField
              label="Arrêter à la première erreur"
              checked={config.stopOnError}
              onChange={(v) => set('stopOnError', v)}
            />
            <SwitchField
              label="Ignorer les doublons"
              checked={config.skipDuplicates}
              onChange={(v) => set('skipDuplicates', v)}
            />
            <SwitchField
              label="Notifier à la fin"
              checked={config.notifyOnCompletion}
              onChange={(v) => set('notifyOnCompletion', v)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Politique de conflits */}
      <Card>
        <CardHeader icon={<ShieldAlert />} iconColor="orange">
          <CardTitle className="text-base">Politique de conflits (entité déjà existante)</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          {CONFLICT_ENTITIES.map((ent) => (
            <SelectField
              key={ent.key}
              label={ent.label}
              value={config.conflictPolicy[ent.key]}
              options={CONFLICT_POLICY_OPTIONS}
              onChange={(v) =>
                set('conflictPolicy', { ...config.conflictPolicy, [ent.key]: v })
              }
            />
          ))}
          <div className="sm:col-span-2">
            <SwitchField
              label="Traiter les doublons internes au fichier comme « ignorer »"
              checked={config.conflictPolicy.treatBatchDuplicatesAsSkip}
              onChange={(v) =>
                set('conflictPolicy', { ...config.conflictPolicy, treatBatchDuplicatesAsSkip: v })
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Correspondance */}
      <Card>
        <CardHeader icon={<Link2 />} iconColor="purple">
          <CardTitle className="text-base">Correspondance (liens entre entités)</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <SelectField
            label="Parent → Élève"
            value={config.matching.parentToStudentStrategy}
            options={PARENT_TO_STUDENT_OPTIONS}
            onChange={(v) => set('matching', { ...config.matching, parentToStudentStrategy: v })}
          />
          <SelectField
            label="Élève → Classe"
            value={config.matching.studentToClassStrategy}
            options={STUDENT_TO_CLASS_OPTIONS}
            onChange={(v) => set('matching', { ...config.matching, studentToClassStrategy: v })}
          />
          <SelectField
            label="Enseignant → Matière"
            value={config.matching.teacherToSubjectStrategy}
            options={TEACHER_TO_SUBJECT_OPTIONS}
            onChange={(v) => set('matching', { ...config.matching, teacherToSubjectStrategy: v })}
          />
          <NumberField
            label="Orphelins tolérés (max)"
            value={config.matching.maxOrphansAllowed}
            min={0}
            max={10000}
            onChange={(v) => set('matching', { ...config.matching, maxOrphansAllowed: v })}
          />
          <div className="space-y-2 sm:col-span-2">
            <SwitchField
              label="Échouer si des orphelins sont détectés"
              checked={config.matching.failOnOrphans}
              onChange={(v) => set('matching', { ...config.matching, failOnOrphans: v })}
            />
            <SwitchField
              label="Comparaison insensible à la casse"
              checked={config.matching.caseInsensitive}
              onChange={(v) => set('matching', { ...config.matching, caseInsensitive: v })}
            />
          </div>
        </CardContent>
      </Card>

      {/* Normalisation */}
      <Card>
        <CardHeader icon={<Wand2 />} iconColor="teal">
          <CardTitle className="text-base">Normalisation des valeurs</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <SelectField
            label="Emails"
            value={config.normalization.emailMode}
            options={EMAIL_MODE_OPTIONS}
            onChange={(v) => set('normalization', { ...config.normalization, emailMode: v })}
          />
          <SelectField
            label="Noms"
            value={config.normalization.nameMode}
            options={NAME_MODE_OPTIONS}
            onChange={(v) => set('normalization', { ...config.normalization, nameMode: v })}
          />
          <SelectField
            label="Téléphones"
            value={config.normalization.phoneMode}
            options={PHONE_MODE_OPTIONS}
            onChange={(v) => set('normalization', { ...config.normalization, phoneMode: v })}
          />
          <div className="space-y-1.5">
            <Label className="text-sm">Pays téléphone par défaut (ISO 2)</Label>
            <Input
              value={config.normalization.defaultPhoneCountry}
              maxLength={2}
              onChange={(e) =>
                set('normalization', {
                  ...config.normalization,
                  defaultPhoneCountry: e.target.value.toUpperCase(),
                })
              }
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-sm">Formats de date acceptés (séparés par des virgules)</Label>
            <Input
              value={config.normalization.dateFormats.join(', ')}
              onChange={(e) =>
                set('normalization', {
                  ...config.normalization,
                  dateFormats: e.target.value
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
            />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <SwitchField
              label="Supprimer les espaces internes superflus"
              checked={config.normalization.trimInternalSpaces}
              onChange={(v) => set('normalization', { ...config.normalization, trimInternalSpaces: v })}
            />
            <SwitchField
              label="Retirer le BOM UTF-8"
              checked={config.normalization.stripBom}
              onChange={(v) => set('normalization', { ...config.normalization, stripBom: v })}
            />
            <SwitchField
              label="Normaliser les accents Unicode"
              checked={config.normalization.normalizeUnicodeAccents}
              onChange={(v) =>
                set('normalization', { ...config.normalization, normalizeUnicodeAccents: v })
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Enrichissement */}
      <Card>
        <CardHeader icon={<Sparkles />} iconColor="pink">
          <CardTitle className="text-base">Enrichissement automatique</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <SwitchField
            label="Générer les emails manquants"
            checked={config.enrichment.generateMissingEmail}
            onChange={(v) => set('enrichment', { ...config.enrichment, generateMissingEmail: v })}
          />
          {config.enrichment.generateMissingEmail && (
            <div className="grid gap-4 sm:grid-cols-2 pl-2">
              <div className="space-y-1.5">
                <Label className="text-sm">Modèle d’email</Label>
                <Input
                  value={config.enrichment.emailTemplate}
                  onChange={(e) =>
                    set('enrichment', { ...config.enrichment, emailTemplate: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Domaine du tenant</Label>
                <Input
                  value={config.enrichment.tenantDomain}
                  onChange={(e) =>
                    set('enrichment', { ...config.enrichment, tenantDomain: e.target.value })
                  }
                />
              </div>
            </div>
          )}

          <SwitchField
            label="Générer un identifiant élève"
            checked={config.enrichment.generateStudentId}
            onChange={(v) => set('enrichment', { ...config.enrichment, generateStudentId: v })}
          />
          {config.enrichment.generateStudentId && (
            <div className="pl-2">
              <div className="space-y-1.5 sm:max-w-sm">
                <Label className="text-sm">Motif d’identifiant</Label>
                <Input
                  value={config.enrichment.studentIdPattern}
                  onChange={(e) =>
                    set('enrichment', { ...config.enrichment, studentIdPattern: e.target.value })
                  }
                />
              </div>
            </div>
          )}

          <SwitchField
            label="Générer un mot de passe temporaire"
            checked={config.enrichment.generateTempPassword}
            onChange={(v) => set('enrichment', { ...config.enrichment, generateTempPassword: v })}
          />
          {config.enrichment.generateTempPassword && (
            <div className="pl-2 sm:max-w-xs">
              <NumberField
                label="Longueur du mot de passe"
                value={config.enrichment.tempPasswordLength}
                min={8}
                max={64}
                onChange={(v) =>
                  set('enrichment', { ...config.enrichment, tempPasswordLength: v })
                }
              />
            </div>
          )}

          <SwitchField
            label="Calculer l’âge depuis la date de naissance"
            checked={config.enrichment.computeAgeFromBirthdate}
            onChange={(v) => set('enrichment', { ...config.enrichment, computeAgeFromBirthdate: v })}
          />
          <SwitchField
            label="Générer un avatar (Gravatar)"
            checked={config.enrichment.generateAvatarFromGravatar}
            onChange={(v) =>
              set('enrichment', { ...config.enrichment, generateAvatarFromGravatar: v })
            }
          />
        </CardContent>
      </Card>

      {/* Actions post-import */}
      <Card>
        <CardHeader icon={<Terminal />} iconColor="cyan">
          <CardTitle className="text-base">Actions post-import</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-sm">Commandes à exécuter (whitelist)</Label>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {ALLOWED_POST_IMPORT_COMMANDS.map((cmd) => {
                const checked = config.postImport.runCommands.includes(cmd);
                return (
                  <label key={cmd} className="flex items-center gap-2 text-xs font-mono">
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => {
                        const next = checked
                          ? config.postImport.runCommands.filter((c) => c !== cmd)
                          : [...config.postImport.runCommands, cmd];
                        set('postImport', { ...config.postImport, runCommands: next });
                      }}
                    />
                    {cmd}
                  </label>
                );
              })}
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <NumberField
              label="Timeout par commande (s)"
              value={config.postImport.commandTimeoutSeconds}
              min={5}
              max={600}
              onChange={(v) => set('postImport', { ...config.postImport, commandTimeoutSeconds: v })}
            />
            <div className="space-y-2">
              <SwitchField
                label="Créer les salles manquantes"
                checked={config.postImport.createMissingClassrooms}
                onChange={(v) =>
                  set('postImport', { ...config.postImport, createMissingClassrooms: v })
                }
              />
              <SwitchField
                label="Créer les groupes par défaut"
                checked={config.postImport.createDefaultGroups}
                onChange={(v) =>
                  set('postImport', { ...config.postImport, createDefaultGroups: v })
                }
              />
              <SwitchField
                label="Vider le cache"
                checked={config.postImport.clearCache}
                onChange={(v) => set('postImport', { ...config.postImport, clearCache: v })}
              />
              <SwitchField
                label="Arrêter si une commande échoue"
                checked={config.postImport.stopOnCommandFailure}
                onChange={(v) =>
                  set('postImport', { ...config.postImport, stopOnCommandFailure: v })
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>

    </div>
  );
}
